import {
  useQuery,
  QueryResult,
  MutationResultPair,
  useMutation
} from 'react-query';
import API from '../../api';
import Endpoints from '../../endpoints';
import { useContext, useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  DBCCollectionResponse, DartCollectionResponse, DBCCollectionOptions, DartCollectionOptions,
  NBCAndConfigDetailsOptions, NBCCollectionOptions, NBCDetailsOptions,
  NBCAndConfigResponse, NBCCollectionResponse,
  NBCDetailsResponse, AddOrDeleteReservationOptions,
  AddOrDeleteReservationResponse, postSingleReservationArgs, PlaceDartOrderResponse,
  PlaceDartOrderOptions, PostDartOrderArgs,
  DartModalConfig,
  DartProductConfig,
  DartProductFlowURLConfig,
  IUseDartProductPage,
  FetchDBCProductInfoOptions,
  DBCProductResponse
} from '../../types/dart';
import GlobalContext from '../../contexts/GlobalContext';
import _ from 'lodash';
import { MODAL_NAMES, getDartModalConfig } from '../../utils/dart';
import { useFetchCloset } from './closet';

export const useFetchNBC = (options?): MutationResultPair<Function, unknown, unknown, unknown> => {
  const endPoints = Endpoints();
  const nbcURL = endPoints.nbc();
  const urlParams = [
    'scope=' + options.scope,
    'cluster=' + options.clusterValue,
    'sourceEventId=' + options.sourceEventId
  ].join('&');
  let nbcData: any = {};

  async function fetchNBCData(): Promise<any> {
    const payload = { userStatus: { availability: { isOptIn: true } } };

    try {
      const response = await API.put(`${nbcURL}?${urlParams}`, { ...payload });
      const { status, data } = response;

      if (status === 200 && data.status && data.status.code !== 1) {
        nbcData = data;
      }

      return nbcData;
    } catch (error) {
      throw error;
    }
  }

  return useMutation((): any => fetchNBCData(), {
    onSuccess: options.onSuccess,
    onError: options.onError
  });
};

export const useFetchQuote = (options?): QueryResult<any> => {
  const endPoints = Endpoints();
  const quoteURL = endPoints.quote();
  const urlParams = [
    'cluster=' + options.clusterValue,
    'sourceEventId=' + options.sourceEventId
  ].join('&');
  let quoteData: any = {};

  async function fetchQuoteData(): Promise<any> {
    const response = await API.get(`${quoteURL}&${urlParams}`);
    const { status, data } = response;

    if (status === 200 && data.status && data.status.code === 0) {
      quoteData = data;
    }

    return quoteData;
  }

  return useQuery('fetchQuote', () => fetchQuoteData(), {
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    onSuccess: options.onSuccess,
    enabled: options.enabled
  });
};

export const useFetchConfig = (options?): QueryResult<any> => {
  const endPoints = Endpoints();
  let module: any = {};

  async function fetchModuleConfig(moduleList): Promise<any> {
    const modules = moduleList.join(',');
    const configUrl = endPoints.config(modules);
    const response: any = await API.get(configUrl);
    const { status, data } = response;

    if (status === 200 && data && data.status && data.status.code === 0) {
      if (data.tenant && data.tenant.configuration) {
        module = data.tenant.configuration;
      }
    }

    return module;
  }

  return useQuery('fetchModuleConfig', () => fetchModuleConfig(options.moduleList), {
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    onSuccess: options.onSuccess,
    enabled: options.enabled
  });
};

export const useFetchDart = (options?): QueryResult<any> => {
  const [dartDetails, setDartDetails] = useState({ nbcData: {}, configuration: {}, quote: {} });
  const [fetchNBCData] = useFetchNBC({
    scope: 'etsDetails,userStatus',
    sourceEventId: options.sourceEventId,
    clusterValue: options.clusterValue
  });
  const fetchQuoteDetails = useFetchQuote({
    sourceEventId: options.sourceEventId,
    clusterValue: options.clusterValue
  });
  const fetchNbcConfig = useFetchConfig({
    moduleList: ['NBC'],
    sourceEventId: options.sourceEventId,
    clusterValue: options.clusterValue
  });

  async function fetchDart(): Promise<any> {
    await Promise.all([fetchNBCData(), fetchNbcConfig.refetch(), fetchQuoteDetails.refetch()])
      .then(([data, nbcConfig, nbcQuote]) => {
        let tmpDartDetails = dartDetails;

        tmpDartDetails.nbcData = data;
        tmpDartDetails.configuration = nbcConfig;
        tmpDartDetails.quote = nbcQuote;
        setDartDetails(tmpDartDetails);
      });

    return dartDetails;
  }

  return useQuery('fetchDart', () => fetchDart(), {
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    onSuccess: options.onSuccess,
    enabled: options.enabled
  });
};

export const useFetchDBCCollection = (options: DBCCollectionOptions): QueryResult<DBCCollectionResponse> => {
  const { globalContext } = useContext(GlobalContext);
  const endPoints = Endpoints();
  const dbcURL = endPoints.dbc();
  const urlParams = [
    'sourceEventId=' + globalContext.emitter.sourceEventId,
    'cluster=' + globalContext.gb.utils.getClusterValue(),
    'query=' + options.queryParams
  ].join('&');
  let dbcCollection: DBCCollectionResponse;

  async function fetchDBCCollection(): Promise<DBCCollectionResponse> {
    try {
      const response = await API.get(`${dbcURL}?${urlParams}`);
      const { status, data } = response;

      if (status === 200 && data.status && data.status.code === 0) {
        dbcCollection = data;
      }

      return dbcCollection;
    } catch (error) {
      throw error;
    }
  }

  return useQuery('fetchDBCCollection', () => fetchDBCCollection(), {
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    enabled: options.enabled
  });
};

export const useFetchNBCCollection = (options: NBCCollectionOptions): QueryResult<NBCCollectionResponse> => {
  const { globalContext } = useContext(GlobalContext);
  const endPoints = Endpoints();
  const nbcCollectionURL = endPoints.nbcCollection();
  const urlParams = [
    'sourceEventId=' + globalContext.emitter.sourceEventId,
    'cluster=' + globalContext.gb.utils.getClusterValue()
  ].join('&');
  let nbcCollection: NBCCollectionResponse;

  async function fetchNBCCollection(): Promise<NBCCollectionResponse> {
    try {
      const response = await API.get(`${nbcCollectionURL}?${urlParams}`);
      const { status, data } = response;

      if (status === 200 && data.status && data.status.code === 0) {
        nbcCollection = data;
      }

      return nbcCollection;
    } catch (error) {
      throw error;
    }
  }

  return useQuery('fetchNBCCollection', () => fetchNBCCollection(), {
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    enabled: options.enabled
  });
};

export const useFetchNBCDetails = (options: NBCDetailsOptions): QueryResult<NBCDetailsResponse> => {
  const { globalContext } = useContext(GlobalContext);
  const endPoints = Endpoints();
  const nbcURL = endPoints.nbc();
  const urlParams = [
    'sourceEventId=' + globalContext.emitter.sourceEventId,
    'cluster=' + globalContext.gb.utils.getClusterValue(),
    'scope=' + options.scope
  ].join('&');
  let nbcData: NBCDetailsResponse;
  const fetchNBCDetails = async function (): Promise<NBCDetailsResponse> {
    try {
      const response = await API.put(`${nbcURL}?${urlParams}`, { userStatus: { availability: { isOptIn: true } } });
      const { status, data } = response;

      if (status === 200 && data.status && data.status.code !== 1) {
        nbcData = data;
      }

      return nbcData;
    } catch (error) {
      throw error;
    }
  };

  return useQuery('fetchNBCDetails', fetchNBCDetails, {
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    enabled: options.enabled,
    onSuccess: (data) => options.onSuccess
  });
};

export const useFetchDartCollection = (options: DartCollectionOptions): QueryResult<DartCollectionResponse> => {
  const { globalContext } = useContext(GlobalContext);
  const nbcCollectionQuery = useFetchNBCCollection({
    enabled: false
  });
  const dbcCollectionQuery = useFetchDBCCollection({
    queryParams: options.queryParams,
    enabled: false

  });
  const nbcDetailsQuery = useFetchNBCDetails({
    scope: options.scope,
    enabled: false
  });
  const closetDetailsQuery = useFetchCloset({
    section: 'atHome',
    clusterValue: globalContext.gb.utils.getClusterValue(),
    sourceEventId: globalContext.emitter.sourceEventId
  })

  async function fetchDartCollection(): Promise<DartCollectionResponse> {
    let dartCollection: DartCollectionResponse;

    try {
      await Promise.all([
        nbcCollectionQuery.refetch(),
        dbcCollectionQuery.refetch(),
        nbcDetailsQuery.refetch(),
        closetDetailsQuery.refetch()
      ]).then(([nbcCollection, dbcCollection, nbcDetails, closetDetails]) => {
        dartCollection = {
          nbcCollection: nbcCollection,
          nbcDetails: nbcDetails,
          dbcCollection: dbcCollection,
          closetDetails: closetDetails
        };
      });

      return dartCollection;
    } catch (error) {
      throw error;
    }
  }

  return useQuery('fetchDartCollection', () => fetchDartCollection(), {
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    onSuccess: options.onSuccess,
    onError: options.onError,
    enabled: options.enabled
  });
};

export const useAddSingleReservation = (options: AddOrDeleteReservationOptions): MutationResultPair<AddOrDeleteReservationResponse, unknown, unknown, unknown> => {
  const { globalContext } = useContext(GlobalContext);
  const endPoints = Endpoints();

  async function postSingleReservation(args: postSingleReservationArgs): Promise<AddOrDeleteReservationResponse> {
    const url = endPoints.addSingleReservation();
    const addSingleReservationURL = `${url}${args.sku}.json?availableSlots=${args.availableSlots}`;
    const urlParams = [
      'sourceEventId=' + globalContext.emitter.sourceEventId,
      'cluster=' + globalContext.gb.utils.getClusterValue()
    ].join('&');

    try {
      const response = await API.post(`${addSingleReservationURL}&${urlParams}`);
      const { data }: { data: AddOrDeleteReservationResponse } = response;

      return { ...data, source: args.source };
    } catch (error) {
      throw error;
    }
  }

  return useMutation((args: postSingleReservationArgs) => postSingleReservation(args), {
    onSuccess: options.onSuccess,
    onError: options.onError
  });
};

export const useDeleteSingleReservation = (options: AddOrDeleteReservationOptions): MutationResultPair<AddOrDeleteReservationResponse, unknown, unknown, unknown> => {
  const { globalContext } = useContext(GlobalContext);
  const endPoints = Endpoints();
  const urlParams = [
    'sourceEventId=' + globalContext.emitter.sourceEventId,
    'cluster=' + globalContext.gb.utils.getClusterValue()
  ].join('&');

  async function deleteSingleReservation(id: string): Promise<AddOrDeleteReservationResponse> {
    const deleteSingleReservationURL = endPoints.deleteSingleReservation(id);

    try {
      const response = await API.delete(`${deleteSingleReservationURL}?${urlParams}`);
      const { data }: { data: AddOrDeleteReservationResponse } = response;

      return data;
    } catch (error) {
      throw error;
    }
  }

  return useMutation((id: string) => deleteSingleReservation(id), {
    onSuccess: options.onSuccess,
    onError: options.onError
  });
};

export const useSizeAdvisor = (options: any): any => {
  const endPoints = Endpoints();
  const { globalContext } = useContext(GlobalContext);
  const sourceEventId = globalContext.emitter.sourceEventId;
  const fetchSizeAdvisor = async (styleId: string): Promise<any> => {
    const sizeAdVisorURL = endPoints.sizeAdvisor(styleId) + `?sourceEventId=${sourceEventId}`;

    try {
      const { data } = await API.get(sizeAdVisorURL);

      return data;
    } catch (error) {
      throw error;
    }
  };

  return { fetchSizeAdvisor: fetchSizeAdvisor };
};

export const usePlaceDartOrder = (options: PlaceDartOrderOptions): MutationResultPair<PlaceDartOrderResponse, unknown, unknown, unknown> => {
  const { globalContext } = useContext(GlobalContext);
  const endPoints = Endpoints();
  const postDartOrderUrl = endPoints.dartOrder();
  const urlParams = [
    'sourceEventId=' + globalContext.emitter.sourceEventId,
    'cluster=' + globalContext.gb.utils.getClusterValue()
  ].join('&');

  async function postDartOrder(args: PostDartOrderArgs): Promise<PlaceDartOrderResponse> {
    const payload = {
      sla: args.sla,
      isRevisedSLA: args.isRevisedSLA,
      skuList: args.skuList,
      dbcSkuList: args.dbcSkuList,
      quote: args.quote,
      paymentToken: args.paymentToken
    };

    try {
      const response = await API.post(`${postDartOrderUrl}?${urlParams}`, { ...payload });
      const { data }: { data: PlaceDartOrderResponse } = response;

      return data;
    } catch (error) {
      throw error;
    }
  }

  return useMutation((args: any) => postDartOrder(args), {
    onSuccess: options.onSuccess,
    onError: options.onError
  });
};

export const useDeleteAllReservation = (options: any): MutationResultPair<unknown, unknown, unknown, unknown> => {
  const { globalContext } = useContext(GlobalContext);
  const endPoints = Endpoints();
  const deleteAllReservationsUrl = endPoints.deleteAllReservations();
  const urlParams = [
    'sourceEventId=' + globalContext.emitter.sourceEventId,
    'cluster=' + globalContext.gb.utils.getClusterValue()
  ].join('&');

  async function deleteAllReservations(idList: any): Promise<any> {
    try {
      const response = await API.delete(`${deleteAllReservationsUrl}?idsList=${idList}&${urlParams}`);
      const { data }: { data: any } = response;

      return data;
    } catch (error) {
      throw error;
    }
  }

  return useMutation((idList: any) => deleteAllReservations(idList), {
    onSuccess: options.onSuccess,
    onError: options.onError
  });
};

export function useDartOnProductPage(options: any): IUseDartProductPage {
  // eslint-disable-next-line max-len
  const [dartFlowConfig, setdartFlowConfig] = useState<DartProductConfig>({ isDartProduct: false, dartModalConfig: {} } as DartProductConfig);
  const { globalContext } = useContext(GlobalContext);
  const location = useLocation();
  const [deleteAllReservations] = useDeleteAllReservation({ onSuccess: (data: any) => data });
  const search = location.search;
  const params = new URLSearchParams(search);
  const PRODUCT_INFO_STORAGE_KEY = globalContext.i18n.getText('dart_product_info_storage_key');

  /**
   * function to remove query param from url, used after getting values from url
   * @param queryParamName to be removed
   */
  function restoreProductPageURL(queryParamName: string): void {
    if (params.has(queryParamName)) {
      params.delete(queryParamName);

      const newUrl = params.toString() ?
        `${window.location.pathname}?${params.toString()}` :
        window.location.pathname;

      window.history.replaceState({}, document.title, newUrl);
    }
  }

  useEffect(() => {
    let flowInfo: DartProductFlowURLConfig;

    try {
      const isDartProductPageFlow = params.has(globalContext.i18n.getText('dart_product_query_key')) || globalContext.gb.utils.getFromStore(PRODUCT_INFO_STORAGE_KEY);

      if (!isDartProductPageFlow) {
        return;
      }

      const stringifiedEncodedProductInfo = params.get(globalContext.i18n.getText('dart_product_query_key'));
      const decodedProductInfo = stringifiedEncodedProductInfo ?
        decodeURIComponent(stringifiedEncodedProductInfo) :
        globalContext.gb.utils.getFromStore(PRODUCT_INFO_STORAGE_KEY);

      flowInfo = JSON.parse(decodedProductInfo);
      restoreProductPageURL(globalContext.i18n.getText('dart_product_query_key'));
      setdartFlowConfig({
        ...dartFlowConfig,
        ...flowInfo,
        isDartProduct: true,
        timerProps: {
          expiryTime: flowInfo.expiryTime,
          reservationIdMap: flowInfo.reservationIdMap,
          availableSlots: flowInfo.availableSlots,
          garmentLeft: flowInfo.availableSlots - flowInfo.reservationInfo.length,
          garmentsPicked: flowInfo.reservationInfo.length
        },
        dartModalConfig: {} as DartModalConfig
      });
      // window.addEventListener('beforeunload', handleBrowserNavigation);
      globalContext.gb.utils.saveToStore(PRODUCT_INFO_STORAGE_KEY, decodedProductInfo);
    } catch (error) {
      // product info not found, previous url is other than daart
      setdartFlowConfig({ isDartProduct: false } as DartProductConfig);
    }

    // eslint-disable-next-line no-unused-expressions
    (): void => {
      // window.removeEventListener('beforeunload', handleBrowserNavigation);
      globalContext.gb.utils.removeFromStore(PRODUCT_INFO_STORAGE_KEY);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  /**
   * opens dart modal by modal name
   * @param modalName string
   */
  function openDartModal(modalName: string) {
    const modalConfig = getDartModalConfig(modalName);

    setdartFlowConfig({
      ...dartFlowConfig,
      dartModalConfig: { ...modalConfig, showModal: !_.isEmpty(modalConfig) }
    });
  }

  /**
   * redirects user to dart page
   */
  const handleCloseReservationExpiredModal = (): void => {
    globalContext.gb.utils.removeFromStore(PRODUCT_INFO_STORAGE_KEY);
    globalContext.gtm.pushVersionedEvent('2.11', 'DartCollection', 'ClickProductBackButton');
    window.location.href = '/pages/dart?returnToDart=true';
  };

  /**
   * set modal states to close it
   */
  function handleDartModalClose(): void {
    setdartFlowConfig({
      ...dartFlowConfig,
      dartModalConfig: {} as DartModalConfig
    });
  }

  /**
   * deletes reservation on timer expiration and open dart modal based on status code
   */
  const handleTimerExpired = async (): Promise<void> => {
    const idsList = (_.values(dartFlowConfig.timerProps.reservationIdMap)).join();

    try {
      const data: any = await deleteAllReservations(idsList);

      if (data?.status?.code === 0) {
        openDartModal(MODAL_NAMES.RESERVED_STYLES_EXPIRED);
      } else {
        openDartModal(MODAL_NAMES.SOMETHING_WENT_WRONG);
      }
    } catch (error) {
      openDartModal(MODAL_NAMES.SOMETHING_WENT_WRONG);
    }
  };

  return {
    dartFlowConfig: dartFlowConfig,
    openDartModal: openDartModal,
    setdartFlowConfig: setdartFlowConfig,
    handleTimerExpired: handleTimerExpired,
    handleDartModalClose: handleDartModalClose,
    handleCloseReservationExpiredModal: handleCloseReservationExpiredModal
  };
}

export const useFetchDBCProductInfo = (options: FetchDBCProductInfoOptions): QueryResult<DBCProductResponse> => {
  const { globalContext } = useContext(GlobalContext);
  const endPoints = Endpoints();
  const nbcURL = endPoints.getDBCProductInfo();
  const urlParams = [
    'cluster=' + globalContext.gb.utils.getClusterValue(),
    'sourceEventId=' + globalContext.emitter.sourceEventId
  ].join('&');
  let dbcProductInfo: DBCProductResponse;
  const fetchDBCProductData = async function (productHandle: string): Promise<DBCProductResponse> {
    try {
      const response = await API.get(`${nbcURL}?productHandle=${productHandle}&${urlParams}`);
      const { status, data } = response;

      if (status === 200 && data.status.code !== 1) {
        dbcProductInfo = data.product;
      }

      return dbcProductInfo;
    } catch (error) {
      throw error;
    }
  };

  return useQuery('fetchDBCProductData', () => fetchDBCProductData(options.productHandle), {
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    enabled: options.enabled
  });
};
